<script lang="ts">
	// INFO Generated by AI. Sorry to future maintainers :)

	let canvas = $state<HTMLCanvasElement | null>(null);

	const WAVE_COLOR = "#00AFF0";
	const WAVE_COUNT = 5;

	let width = $state(0);
	let height = $state(0);
	let globalMouseY = $state(0);
	let mouseActivity = $state(0);
	let lastMouseX = $state(0);
	let lastMouseY = $state(0);
	let lastActivityTime = $state(Date.now());

	const waves = Array.from({ length: WAVE_COUNT }, (_, i) => {
		return {
			amplitude: 15 + i * 5,
			frequency: 0.003 + i * 0.0015, // More varied base widths (wavelengths)
			// Frequency modulation parameters
			freqVariance: 0.0005 + i * 0.0002,
			freqModFreq: 0.001 + i * 0.0005,
			phase: i * Math.PI * 0.4,
			// More varied speeds for a more natural "water" feel
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			baseSpeed: [0.008, 0.015, 0.012, 0.02, 0.01][i % 5]!,
			// Speed modulation parameters to make speeds change over time
			speedVariance: 0.003 + i * 0.001,
			speedModFreq: 0.002 + i * 0.001,
			opacity: 0.2 + (i / WAVE_COUNT) * 0.5,
			// Vertical oscillation (bouncing) parameters
			bounceAmplitude: 10 + i * 2,
			bounceSpeed: 0.01 + i * 0.005,
			// Per-wave stretch reaction parameters
			stretchFactor: 0.8 + i * 0.1, // Base reaction weight
			stretchModFreq: 0.0015 + i * 0.0005,
			stretchModAmplitude: 0.2,
		};
	});

	const CANVAS_PADDING = 200; // Extra space to prevent clipping

	$effect(() => {
		if (!canvas) {
			return;
		}
		const ctx = canvas.getContext("2d");
		if (!ctx) {
			return;
		}

		let animationFrame: number;
		let time = 0;
		let currentStretch = 1;
		let currentMovementFactor = 1;
		// Keep track of total phase for each wave since speed is now variable
		const waveOffsets = waves.map(() => 0);

		const render = (): void => {
			// Speed up based on mouse activity
			// Decay mouse activity over time
			const now = Date.now();
			const elapsedSinceActivity = now - lastActivityTime;
			if (elapsedSinceActivity > 50) {
				mouseActivity *= 0.98;
			}

			// Target movement factor: 1 (slow) to 2 (fast)
			const targetMovementFactor = 1 + Math.min(1, mouseActivity / 50);
			const lerpFactor = targetMovementFactor > currentMovementFactor ? 0.01 : 0.03;
			currentMovementFactor += (targetMovementFactor - currentMovementFactor) * lerpFactor;

			time += 0.4 * currentMovementFactor;
			const totalHeight = height + CANVAS_PADDING * 2;
			ctx.clearRect(0, 0, width, totalHeight);

			// Mouse reaction: stretch vertically
			// Least at the top (stretch = 1), most at the bottom
			// We use window.innerHeight to normalize the mouse position
			const windowHeight = window.innerHeight || 1;
			const normalizedY = Math.max(0, Math.min(1, globalMouseY / windowHeight));

			// Maximum stretch factor (e.g., 4x)
			const MAX_STRETCH = 4;
			const targetStretch = 1 + normalizedY * (MAX_STRETCH - 1);

			// Tween the stretch factor for smoothness
			// Adjust 0.05 to change the speed of the transition (lower = slower)
			currentStretch += (targetStretch - currentStretch) * 0.05;

			waves.forEach((wave, i) => {
				ctx.beginPath();
				ctx.strokeStyle = WAVE_COLOR;
				ctx.globalAlpha = wave.opacity;
				ctx.lineWidth = width < 768 ? 1 : 2;

				// Calculate dynamic speed
				const speedMod = Math.sin(time * wave.speedModFreq) * wave.speedVariance;
				const currentSpeed = wave.baseSpeed + speedMod;

				// Update offset based on current speed
				waveOffsets[i] = (waveOffsets[i] ?? 0) + currentSpeed * 0.4 * currentMovementFactor;

				const bounceOffset = Math.sin(time * wave.bounceSpeed) * wave.bounceAmplitude;

				// Dynamic per-wave stretch calculation
				const stretchMod = Math.sin(time * wave.stretchModFreq) * wave.stretchModAmplitude;
				const waveSpecificStretch = currentStretch * (wave.stretchFactor + stretchMod);

				// Dynamic per-wave frequency (width) modulation
				const freqMod = Math.sin(time * wave.freqModFreq) * wave.freqVariance;
				const currentFreq = wave.frequency + freqMod;

				for (let x = 0; x <= width; x += 5) {
					const y =
						totalHeight / 2 +
						bounceOffset +
						Math.sin(x * currentFreq + wave.phase + (waveOffsets[i] ?? 0)) * wave.amplitude * waveSpecificStretch;

					if (x === 0) {
						ctx.moveTo(x, y);
					} else {
						ctx.lineTo(x, y);
					}
				}
				ctx.stroke();
			});

			animationFrame = requestAnimationFrame(render);
		};

		animationFrame = requestAnimationFrame(render);

		return (): void => {
			cancelAnimationFrame(animationFrame);
		};
	});

	function handleMouseMove(e: MouseEvent): void {
		globalMouseY = e.clientY;

		const dx = e.clientX - lastMouseX;
		const dy = e.clientY - lastMouseY;
		const distance = Math.sqrt(dx * dx + dy * dy);

		mouseActivity += distance;
		// Clamp activity to prevent extreme speeds
		if (mouseActivity > 200) {
			mouseActivity = 200;
		}

		lastMouseX = e.clientX;
		lastMouseY = e.clientY;
		lastActivityTime = Date.now();
	}
</script>

<svelte:window onmousemove={handleMouseMove} />

<div class="h-full w-full overflow-visible" bind:clientWidth={width} bind:clientHeight={height}>
	<canvas
		bind:this={canvas}
		{width}
		height={height + CANVAS_PADDING * 2}
		style:margin-top="-{CANVAS_PADDING}px"
		class="block w-full pointer-events-none"
	></canvas>
</div>
